<template>
  <div ref="productTag" class="tag" :class="direction">
    <transition name="scale">
      <div v-if="isTagVisible">
        <div class="tag-body" :class="direction" @click.stop="goToProduct">
          <ResponsiveImage
            v-if="imageUrl"
            :url="imageUrl"
            :widths="[80]"
            :breakpoints="[80]"
            :skeleton="true"
          ></ResponsiveImage>

          <div class="productDetail">
            <p>{{ title }}</p>
            <span class="originalPrice" v-if="hasSalePrice"> {{ originalPrice }} </span>
            <span class="newPrice"> {{ price }} </span>
          </div>

          <i class="af af-chevron-right" />
        </div>
      </div>
    </transition>

    <transition name="scale">
      <div v-if="isAnchorVisible" class="tag-anchor" :class="direction" />
    </transition>
  </div>
</template>

<script>
import CountryUtils from '@/components/commons/CountryUtils.js';
import Tracking from '@/components/commons/Tracking.js';
import { nextTick } from 'vue';
import LocationService from '@/services/LocationService';
import useCommon from '@/compositions/useCommon';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';

export default {
  name: 'ProductImageTag',
  components: {
    ResponsiveImage
  },
  props: {
    product: Object,
    tracking: Object,
    naturals: {
      type: Object,
      required: true,
    },
    direction: String,
    isMobile: {
      type: Boolean,
      default: false,
    },
    isActiveSlide: Boolean,
    isTagVisible: Boolean,
    tagsEnabled: Boolean,
  },
  data() {
    return {
      productData: false,
      isAnchorVisible: false,
    };
  },

  computed: {
    title() {
      if (this.productData) {
        return this.productData.title;
      }
      return 'Loading...';
    },

    hasSalePrice() {
      if (this.productData) {
        return this.productData.salePrice[LocationService.getCurrencyId()] != null;
      }
      return false;
    },

    salePrice() {
      if (this.productData) {
        if (this.productData.salePrice[LocationService.getCurrencyId()] != null) {
          return this.formatCurrency(this.productData.salePrice[LocationService.getCurrencyId()]);
        }
      }
      return ' ';
    },

    originalPrice() {
      if (this.productData) {
        return this.formatCurrency(this.productData.basePrice[LocationService.getCurrencyId()]);
      }
      return '   ';
    },

    price() {
      if (this.productData) {
        return this.hasSalePrice ? this.salePrice : this.originalPrice;
      }
      return ' ';
    },

    imageUrl(){
      return this.productData?.imageUrl;
    }
  },

  watch: {
    activeFileProducts() {
      this.setProductData(this.activeFileProducts);
    },

    isActiveSlide(val) {
      if (val) {
        setTimeout(() => {
          this.revealAnchor();
        }, 500);
      }
    },
  },

  mounted() {
    window.addEventListener('resize', this.positionTag);
    this.positionTag(false);

    if (this.isActiveSlide) {
      setTimeout(() => {
        this.revealAnchor();
      }, 1000);
    }
  },

  unmounted() {
    window.removeEventListener('resize', this.positionTag);
  },

  setup() {
    const { activeFileProducts } = useCommon();
    return {
      activeFileProducts,
    };
  },

  methods: {
    setProductData(products) {
      if (!products) {
        this.productData = false;
        return false;
      }
      const foundProd = products.find((x) => parseInt(x.id, 10) === this.product.id);
      if (foundProd) {
        this.productData = { id: foundProd.id, ...foundProd.attributes };
      } else {
        this.productData = false;
      }
    },

    positionTag(synthetic = true) {
      nextTick(() => {
        if (this.$refs.productTag) {
          this.$refs.productTag.style.top = `${(this.product.y / (this.naturals.naturalHeight * 2)) * this.naturals.getClientRects()[0].height}px`;
          this.$refs.productTag.style.left = `${(this.product.x / (this.naturals.naturalWidth * 2)) * this.naturals.getClientRects()[0].width}px`;
          if (synthetic) {
            this.isAnchorVisible = false;
          }
        }
      });
    },

    goToProduct(e) {
      if (this.productData) {
        const tracked = this.tracking;
        tracked.product = this.productData;

        Tracking.track('RoomIdeasProductTagView', tracked);

        Tracking.track('room-ideas-image-view', { image_number: this.tracking.index, id: this.tracking.file });

        if (this.isMobile) {
          Tracking.track('image_tagging_product_click', {
            channel: 'IMAGE_TAG',
            image_number: tracked.index,
            room: tracked.room,
            room_images_num: tracked.roomImagesNum,
            id: tracked.file,
            variant: 1,
          });
        }

        window.location.href = this.productData.url;
      }
    },

    revealAnchor() {
      if (this.isTagVisible || !this.tagsEnabled) {
        return;
      }
      if (this.isActiveSlide && !this.anchorsVisible) {
        this.anchorsVisible = true;
        this.toggleAnchorVisibility();
        setTimeout(() => {
          this.toggleAnchorVisibility();
          this.anchorsVisible = false;
        }, 2000);
      }
    },

    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('en-US');

      const currencyPrefix = CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol;
      return `${currencyPrefix}${formatter.format(amount / 100)}`;
    },

    toggleAnchorVisibility() {
      this.isAnchorVisible = !this.isAnchorVisible;
    },
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/productTags';
@import '@/pixel/style/variables';

.tag {
  position: absolute;
  z-index: 19;
}

p {
  display: block;
  position: relative;
  width: 95%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.tag-body {
  display: flex;
  justify-content: space-between;


  &:active{
    background-color: var(--grey-ultra-light);
  }

  .image{
    width: 80px;
    margin-right: 10px;
    height: auto;
    ::v-deep(img) {
      object-fit: contain;
    }
  }

  .productDetail{
    min-width: calc(100% - 80px);
  }
  .af-chevron-right {
    /* float: right; */
    align-self: center;
  }
  
  @media (max-width: $tablet-breakpoint) {
    width: 160px;
  }
  cursor: pointer;
  &.top {
    top: -10px;
    transform: translate(-50%, 90%);
  }

  &.right {
    left: -20px;
    transform: translate(-97%, -50%);
  }

  &.bottom {
    top: 10px;
    transform: translate(-50%, -190%);
  }

  &.left {
    left: 40px;
    transform: translate(-3%, -50%);
  }

  @media (max-width: $tablet-breakpoint) {
    &.top {
      transform: translate(-53%, 110%);
    }

    &.right {
      transform: translate(-100%, -60%);
    }

    &.bottom {
      transform: translate(-53%, -220%);
    }

    &.left {
      transform: translate(-10%, -60%);
    }
  }
  .originalPrice {
    margin-right: 8px;
    @media (max-width: $tablet-breakpoint) {
      font-size: var(--font-size-12);
    }
    font-size: var(--font-size-14);
    text-decoration: line-through;
    color: var(--grey-medium);
  }
  .newPrice {
    font-family: var(--font-bold);
    @media (max-width: $tablet-breakpoint) {
      font-size: var(--font-size-12);
    }
    font-size: var(--font-size-14);
  }
}

.tag-anchor {
  transform-origin: center;
  &.top {
    top: 25px;
    left: -17px;
  }

  &.right {
    top: -7.5px;
    left: 0px;
  }

  &.bottom {
    top: -40px;
    left: -17px;
  }

  &.left {
    top: -7.5px;
    left: 0px;
  }
}
</style>
